import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, ScrollText } from "lucide-react";

// Compact color mapping keyed by the action prefix.
const ACTION_TINT = {
  "auth.login": "bg-slate-100 text-slate-700",
  "auth.login_failed": "bg-red-100 text-red-800",
  "auth.login_blocked": "bg-red-100 text-red-800",
  "auth.logout": "bg-slate-100 text-slate-700",
  "user.create": "bg-emerald-100 text-emerald-800",
  "user.update": "bg-blue-100 text-blue-800",
  "order.create": "bg-emerald-100 text-emerald-800",
  "order.update": "bg-blue-100 text-blue-800",
  "order.delete": "bg-red-100 text-red-800",
  "claim.create": "bg-purple-100 text-purple-800",
  "claim.decision": "bg-amber-100 text-amber-800",
  "b2b.decision": "bg-amber-100 text-amber-800",
  "b2b.invoice_upload": "bg-emerald-100 text-emerald-800",
  "b2b.accountant_remark": "bg-orange-100 text-orange-800",
  "file.download": "bg-slate-100 text-slate-700",
};

export default function Audits() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/audits", { params });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const renderMeta = (meta) => {
    if (!meta || Object.keys(meta).length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="font-mono text-[11px] whitespace-pre-wrap break-all">
        {Object.entries(meta).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join("  ")}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-muted-foreground" />
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground">Who did what, when — across auth, orders, claims, B2B and file access.</p>
        </div>
      </div>

      <Card className="p-4 space-y-3" data-testid="audits-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Search (action, actor, target)</Label>
            <Input
              placeholder="e.g. order.delete, Admin, ORD..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              data-testid="audits-search"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="audits-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="audits-end" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Showing <span className="font-medium">{items.length}</span> of <span className="font-medium">{total}</span> events
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="audits-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="audits-empty">
                    No audit events.
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(it.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_TINT[it.action] || "bg-gray-100 text-gray-700"}`}>
                      {it.action}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {it.actor_name ? (
                      <>
                        <div className="font-medium">{it.actor_name}</div>
                        <div className="text-muted-foreground">{it.actor_role}</div>
                      </>
                    ) : <span className="text-muted-foreground">system / unauth</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {it.target_type ? (
                      <>
                        <div className="text-muted-foreground">{it.target_type}</div>
                        <div className="font-mono">{it.target_id}</div>
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{renderMeta(it.meta)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
